import React from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { BookIcon, UsersIcon, EditIcon, TargetIcon, CheckIcon } from '../components/Icons';
import { AnimatedHero } from '../components/AnimatedHero';

export const LandingPage = () => {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      color: theme.colors.textPrimary,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main>
        {/* Hero Section */}
        <AnimatedHero />

        {/* Three-Column Benefits */}
        <section style={{
          backgroundColor: theme.colors.backgroundAlt,
          padding: '80px 40px',
          borderTop: `1px solid ${theme.colors.border}`,
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
          }}>
            <h3 style={{
              fontSize: '40px',
              fontWeight: '400',
              textAlign: 'center',
              marginBottom: '60px',
              color: theme.colors.textPrimary,
            }}>
              For Everyone in the Literary Community
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '40px',
            }}>
              {/* Writers */}
              <div style={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.borderRadius.xlarge,
                padding: '40px',
                border: `1px solid ${theme.colors.border}`,
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '56px',
                  height: '56px',
                  backgroundColor: theme.colors.primaryLight,
                  borderRadius: theme.borderRadius.large,
                  color: theme.colors.primary,
                  marginBottom: '24px',
                }}>
                  <EditIcon />
                </div>
                <h4 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  For Writers
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: theme.colors.textSecondary,
                }}>
                  <li style={{ marginBottom: '12px' }}>✓ Find beta readers who love your genre</li>
                  <li style={{ marginBottom: '12px' }}>✓ Get detailed, constructive feedback</li>
                  <li style={{ marginBottom: '12px' }}>✓ Connect with literary agents</li>
                  <li style={{ marginBottom: '12px' }}>✓ Track your query submissions</li>
                  <li>✓ Build your writer network</li>
                </ul>
              </div>

              {/* Beta Readers */}
              <div style={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.borderRadius.xlarge,
                padding: '40px',
                border: `1px solid ${theme.colors.border}`,
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '56px',
                  height: '56px',
                  backgroundColor: theme.colors.primaryLight,
                  borderRadius: theme.borderRadius.large,
                  color: theme.colors.primary,
                  marginBottom: '24px',
                }}>
                  <BookIcon />
                </div>
                <h4 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  For Beta Readers
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: theme.colors.textSecondary,
                }}>
                  <li style={{ marginBottom: '12px' }}>✓ Discover manuscripts before publication</li>
                  <li style={{ marginBottom: '12px' }}>✓ Read in your favorite genres</li>
                  <li style={{ marginBottom: '12px' }}>✓ Help shape great stories</li>
                  <li style={{ marginBottom: '12px' }}>✓ Connect directly with authors</li>
                  <li>✓ Build your reading portfolio</li>
                </ul>
              </div>

              {/* Agents */}
              <div style={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.borderRadius.xlarge,
                padding: '40px',
                border: `1px solid ${theme.colors.border}`,
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '56px',
                  height: '56px',
                  backgroundColor: theme.colors.primaryLight,
                  borderRadius: theme.borderRadius.large,
                  color: theme.colors.primary,
                  marginBottom: '24px',
                }}>
                  <TargetIcon />
                </div>
                <h4 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '16px',
                  color: theme.colors.textPrimary,
                }}>
                  For Literary Agents
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: theme.colors.textSecondary,
                }}>
                  <li style={{ marginBottom: '12px' }}>✓ Receive quality-vetted submissions</li>
                  <li style={{ marginBottom: '12px' }}>✓ Manage your query inbox efficiently</li>
                  <li style={{ marginBottom: '12px' }}>✓ Share your MSWL with writers</li>
                  <li style={{ marginBottom: '12px' }}>✓ Track manuscript requests</li>
                  <li>✓ Find your next bestseller</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '80px 40px',
        }}>
          <h3 style={{
            fontSize: '40px',
            fontWeight: '600',
            textAlign: 'center',
            marginBottom: '60px',
            color: theme.colors.textPrimary,
            letterSpacing: '-0.01em',
          }}>
            How It Works
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '40px',
          }}>
            {[
              {
                icon: <BookIcon />,
                title: 'Submit Your Manuscript',
                description: 'Upload your completed manuscript with a synopsis and sample chapter. All submissions are reviewed for quality.',
              },
              {
                icon: <UsersIcon />,
                title: 'Get Matched',
                description: 'Our system connects you with beta readers who love your genre and have the right expertise for your work.',
              },
              {
                icon: <CheckIcon />,
                title: 'Receive Feedback',
                description: 'Get thoughtful, detailed feedback within your agreed timeframe. Communicate directly with your readers.',
              },
            ].map((step, index) => (
              <div key={index} style={{
                backgroundColor: theme.colors.white,
                borderRadius: theme.borderRadius.xlarge,
                padding: '40px',
                border: `1px solid ${theme.colors.border}`,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '56px',
                  height: '56px',
                  backgroundColor: theme.colors.primaryLight,
                  borderRadius: theme.borderRadius.large,
                  color: theme.colors.primary,
                  marginBottom: '24px',
                }}>
                  {step.icon}
                </div>
                <h4 style={{
                  fontSize: '22px',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: theme.colors.textPrimary,
                  letterSpacing: '-0.01em',
                }}>
                  {step.title}
                </h4>
                <p style={{
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: theme.colors.textSecondary,
                  margin: 0,
                }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
